import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openWindow } from "../../Slices/windowsSlice";
import {
  fetchNotifiesThunk,
  markNotificationRead,
  markAllReadLocal,
} from "../../Slices/socketemail";

/**
 * NotificationsPanel — the tray flyout that surfaces the EXISTING notification
 * system (state.emailSocket.notificationData) in the Webtop. Reuses the same
 * thunks (fetch / mark-read) and the same type→target routing as the legacy
 * Notify dropdown, but opens records as WINDOWS via openWindow.
 *
 * Note: the legacy socket listener (NotificationListener) lives in AppContainer,
 * which isn't mounted on the /webtop route — so we refresh on open via
 * fetchNotifiesThunk. (Live socket push in the webtop comes with the rollout/
 * cutover when the listener is mounted in the shell.)
 */

// Resolve a notification to a deep-link window target (mirrors notifyHeader.js).
const targetFor = (item) => {
  if (item.leadId) {
    return { path: `/sales/${item.leadId}`, title: item.leadName || item.agencyName || "Lead" };
  }
  switch (item.type) {
    case "leave":
      return { path: "/adminleaves", title: "Leave Applications" };
    case "Job Requisition":
      return { path: "/JobPosting", title: "Job Requisition" };
    case "followup":
    case "workflow":
      return { path: "/sales", title: "Leads" };
    default:
      return null;
  }
};

const timeAgo = (iso) => {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const NotificationsPanel = ({ onClose }) => {
  const dispatch = useDispatch();
  const notifications = useSelector((s) => s.emailSocket?.notificationData) || [];
  const unreadCount = useSelector((s) => s.emailSocket?.unreadCount) || 0;
  const userId = useSelector((s) => s.loginUserInfo?.loginUserDetails?.id);

  // Refresh on open.
  useEffect(() => {
    if (userId) dispatch(fetchNotifiesThunk({ userId }));
  }, [dispatch, userId]);

  const onItemClick = (item) => {
    if (userId && !item.isRead) {
      dispatch(markNotificationRead({ userId, notificationId: item.id }));
    }
    const target = targetFor(item);
    if (target) dispatch(openWindow(target));
    onClose();
  };

  const markAll = () => {
    if (userId) dispatch(markNotificationRead({ userId }));
    dispatch(markAllReadLocal());
  };

  return (
    <div className="webtop-flyout webtop-notif-flyout" role="dialog" aria-label="Notifications">
      <div className="webtop-notif__head">
        <span className="webtop-notif__title">Notifications</span>
        {unreadCount > 0 && (
          <button type="button" className="webtop-notif__markall" onClick={markAll}>
            Mark all read
          </button>
        )}
      </div>

      <div className="webtop-notif__list">
        {notifications.length === 0 && (
          <div className="webtop-notif__empty">You're all caught up.</div>
        )}
        {notifications.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`webtop-notif__item${item.isRead ? "" : " webtop-notif__item--unread"}`}
            onClick={() => onItemClick(item)}
          >
            {!item.isRead && <span className="webtop-notif__dot" />}
            <span className="webtop-notif__text">
              <span className="webtop-notif__msg">{item.message}</span>
              <span className="webtop-notif__time">{timeAgo(item.createdAt)}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default NotificationsPanel;
